import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import type {
  CellValueChangedEvent,
  ColDef,
  GridReadyEvent,
} from 'ag-grid-community';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { Button } from '@/components/ui/button.tsx';
import type { ProductCatalogItem } from "@ozon-ai/ozon/contracts";
import type { PurchaseResponse } from "@ozon-ai/procurement/contracts";
import { useThemePreference } from '../context/ThemePreferenceContext';
import { buildPurchaseGridTheme } from '../theme/agGridTheme';
import { OfferIdCellEditor } from './OfferIdCellEditor';
import { OfferIdCellRenderer, type PurchaseGridRow } from './OfferIdCellRenderer';
import '../styles/ag-grid.css';

ModuleRegistry.registerModules([AllCommunityModule]);

export type PurchaseGridHandle = {
  addRow: () => void;
  removeSelected: () => void;
};

interface PurchaseGridProps {
  rows: PurchaseGridRow[];
  catalog: ProductCatalogItem[];
  readOnly: boolean;
  onRowsChange: Dispatch<SetStateAction<PurchaseGridRow[]>>;
  /** `none` — кнопки строк выносятся в CardFooter родителя */
  toolbar?: 'top' | 'none';
}

function formatRub(value: number): string {
  return value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export const PurchaseGrid = forwardRef<PurchaseGridHandle, PurchaseGridProps>(function PurchaseGrid(
  { rows, catalog, readOnly, onRowsChange, toolbar = 'top' },
  ref,
) {
  const { resolvedColorScheme } = useThemePreference();
  const gridRef = useRef<AgGridReact<PurchaseGridRow>>(null);
  const isDarkTheme = resolvedColorScheme === 'dark';
  const gridTheme = useMemo(
    () => buildPurchaseGridTheme(isDarkTheme),
    [isDarkTheme],
  );

  const handleProductSelect = useCallback(
    (rowId: string, item: ProductCatalogItem | null, freeText: string) => {
      onRowsChange((prevRows) =>
        prevRows.map((row) => {
          if (row.id !== rowId) {
            return row;
          }
          if (item) {
            return {
              ...row,
              offerId: item.offerId,
              ozonSku: item.ozonSku,
              productName: item.name,
            };
          }
          const catalogItem = catalog.find((entry) => entry.offerId === freeText);
          return {
            ...row,
            offerId: freeText,
            ozonSku: catalogItem?.ozonSku,
            productName: catalogItem?.name || row.productName,
          };
        })
      );
    },
    [catalog, onRowsChange]
  );

  const columnDefs = useMemo<ColDef<PurchaseGridRow>[]>(
    () => [
      {
        colId: 'productName',
        headerName: 'Название',
        flex: 3,
        minWidth: 160,
        editable: !readOnly,
        valueGetter: (params) => params.data?.offerId ?? '',
        cellRenderer: OfferIdCellRenderer,
        cellEditor: OfferIdCellEditor,
        cellEditorPopup: true,
        cellEditorParams: {
          catalog,
          onProductSelect: handleProductSelect,
        },
        valueSetter: (params) => {
          const offerId = String(params.newValue ?? '').trim();
          const item = catalog.find((entry) => entry.offerId === offerId);
          params.data.offerId = offerId;
          if (item) {
            params.data.ozonSku = item.ozonSku;
            params.data.productName = item.name;
          } else if (offerId) {
            params.data.productName = params.data.productName || offerId;
          }
          return true;
        },
      },
      {
        field: 'ozonSku',
        headerName: 'Ozon SKU',
        flex: 1,
        minWidth: 90,
        editable: false,
      },
      {
        field: 'quantity',
        headerName: 'Кол-во',
        flex: 1,
        minWidth: 72,
        editable: !readOnly,
        type: 'numericColumn',
        valueParser: (params) => Number(params.newValue) || 0,
      },
      {
        field: 'unitPriceRub',
        headerName: 'Цена, ₽',
        flex: 1,
        minWidth: 88,
        editable: !readOnly,
        type: 'numericColumn',
        valueParser: (params) => Number(params.newValue) || 0,
        valueFormatter: (params) => formatRub(Number(params.value) || 0),
      },
      {
        field: 'linePurchaseRub',
        headerName: 'Сумма, ₽',
        flex: 1,
        minWidth: 88,
        editable: false,
        valueFormatter: (params) => formatRub(Number(params.value) || 0),
      },
      {
        field: 'unitPriceWithDeliveryRub',
        headerName: 'С доставкой, ₽',
        flex: 1,
        minWidth: 100,
        editable: false,
        valueFormatter: (params) => formatRub(Number(params.value) || 0),
      },
    ],
    [catalog, handleProductSelect, readOnly]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      filter: false,
      resizable: true,
      suppressMovable: true,
    }),
    []
  );

  function onCellValueChanged(event: CellValueChangedEvent<PurchaseGridRow>) {
    if (readOnly || !event.data) {
      return;
    }
    onRowsChange((prevRows) =>
      prevRows.map((row) =>
        row.id === event.data!.id ? { ...row, ...event.data! } : row
      )
    );
  }

  const removeSelected = useCallback(() => {
    const selected = gridRef.current?.api.getSelectedRows() || [];
    if (selected.length === 0) {
      return;
    }
    const selectedIds = new Set(selected.map((row) => row.id));
    onRowsChange(rows.filter((row) => !selectedIds.has(row.id)));
  }, [onRowsChange, rows]);

  const addRow = useCallback(() => {
    const newRow: PurchaseGridRow = {
      id: crypto.randomUUID(),
      offerId: '',
      quantity: 1,
      unitPriceRub: 0,
      linePurchaseRub: 0,
      unitPriceWithDeliveryRub: 0,
    };
    onRowsChange([...rows, newRow]);
  }, [onRowsChange, rows]);

  useImperativeHandle(ref, () => ({ addRow, removeSelected }), [addRow, removeSelected]);

  function refreshGridLayout() {
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }
    api.sizeColumnsToFit();
    api.refreshCells({ force: true });
  }

  function onGridReady(event: GridReadyEvent) {
    event.api.sizeColumnsToFit();
  }

  useEffect(() => {
    refreshGridLayout();
  }, [gridTheme, rows.length]);

  return (
    <div className="flex min-h-[min(65vh,560px)] flex-1 flex-col gap-2">
      {!readOnly && toolbar === 'top' ? (
        <div className="mb-2 flex flex-wrap gap-2 px-2">
          <Button type="button" variant="ghost" size="sm" onClick={addRow}>
            + Строка
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={removeSelected}>
            Удалить выбранные
          </Button>
        </div>
      ) : null}
      <div className="ag-grid-host ag-grid-host--fill min-h-0 flex-1">
        <AgGridReact<PurchaseGridRow>
          ref={gridRef}
          theme={gridTheme}
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          context={{ catalog }}
          popupParent={document.body}
          rowSelection={readOnly ? undefined : { mode: 'multiRow', checkboxes: true, headerCheckbox: true }}
          stopEditingWhenCellsLoseFocus
          onCellValueChanged={onCellValueChanged}
          onGridReady={onGridReady}
          getRowId={(params) => params.data.id}
          overlayNoRowsTemplate="Нет строк — добавьте позиции"
        />
      </div>
    </div>
  );
});

function resolvePurchaseLines(purchase: PurchaseResponse) {
  if (purchase.lines?.length) {
    return purchase.lines;
  }
  try {
    const parsed = JSON.parse(purchase.linesJson) as PurchaseGridRow[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function purchaseToGridRows(purchase: PurchaseResponse): PurchaseGridRow[] {
  return resolvePurchaseLines(purchase).map((line) => ({
    id: line.id,
    offerId: line.offerId,
    ozonSku: line.ozonSku,
    productName: line.productName,
    quantity: line.quantity,
    unitPriceRub: line.unitPriceRub,
    linePurchaseRub: line.linePurchaseRub,
    unitPriceWithDeliveryRub: line.unitPriceWithDeliveryRub,
  }));
}

export function gridRowsToLineInputs(rows: PurchaseGridRow[]) {
  return rows.map((row) => ({
    id: row.id,
    offerId: row.offerId,
    ozonSku: row.ozonSku,
    productName: row.productName,
    quantity: row.quantity,
    unitPriceRub: row.unitPriceRub,
  }));
}
